import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { dashboardQuery, type Period } from '../../lib/queries'
import { kg, nairaShort, num, short } from '../../lib/format'
import { Card, PageHeader, PeriodSelect, StatCard, cx, kgStat } from '../../components/ui'
import { BarChart, Donut, HBars, LineChart } from '../../components/charts'

export const Route = createFileRoute('/_app/')({
  validateSearch: (s: Record<string, unknown>): { period?: Period } => ({ period: ['today', 'week', 'quarter', 'year'].includes(s.period as string) ? (s.period as Period) : undefined }),
  loaderDeps: ({ search }) => ({ period: search.period ?? 'month' }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(dashboardQuery(deps.period)),
  component: Dashboard,
})

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const REGION_COLORS = ['#d09f86', '#566577', '#1c1c1c', '#f86822', '#fec1a4', '#ffb693']
const STATE_COLORS = ['#f86822', '#7c7c7c', '#ffb693', '#c4c4c4']

function Dashboard() {
  const period = Route.useSearch().period ?? 'month'
  const navigate = Route.useNavigate()
  const { data } = useSuspenseQuery(dashboardQuery(period))
  const [tab, setTab] = useState<'sales' | 'volume' | 'status'>('sales')
  const k = data.kpis
  const nairaAxis = (v: number) => nairaShort(v)
  const upToToday = <T,>(arr: T[]) => arr.map((v, i) => (i <= data.week.todayIndex ? v : null))

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Sales, volume and customers across all your outlets."
        actions={<PeriodSelect value={period} onChange={(p) => navigate({ search: { period: p === 'month' ? undefined : p }, replace: true })} />} />
      <div className="grid grid-cols-2 gap-4 lg:gap-7 xl:grid-cols-4">
        <StatCard big tone="peach" label="Volume sold" value={kgStat(k.volumeKg)} change={k.volumeChange} />
        <StatCard big tone="stone" label="Orders received" value={short(k.ordersReceived)} change={k.ordersChange} />
        <StatCard big tone="peach" label="New Customers" value={num(k.newCustomers)} change={k.newCustomersChange} />
        <StatCard big tone="stone" label="Active Customers" value={short(k.activeCustomers)} change={k.activeChange} />
      </div>

      <div className="mt-7 grid gap-7 lg:grid-cols-[1fr_202px] 2xl:grid-cols-[1fr_300px]">
        <Card className="min-w-0 p-6">
          <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            {([['sales', 'Total Sales'], ['volume', 'Volume'], ['status', 'Operating Status']] as const).map(([t, l]) => (
              <button key={t} onClick={() => setTab(t)} className={cx(tab === t ? 'font-semibold text-ink' : 'text-muted hover:text-ink')}>{l}</button>
            ))}
            {tab !== 'status' && (
              <>
                <span className="hidden h-5 w-px bg-black/20 sm:block" />
                <span className="flex items-center gap-1.5 text-xs"><span className="size-1.5 rounded-full bg-ink" />Current Week</span>
                <span className="flex items-center gap-1.5 text-xs"><span className="size-1.5 rounded-full bg-brand" />Previous Week</span>
              </>
            )}
          </div>
          {tab === 'status' ? (
            <div className="grid min-h-[236px] grid-cols-2 content-center gap-4 sm:grid-cols-4">
              {[
                ['Meters online', `${data.operating.online}/${data.operating.devices}`, 'reported in the last 24h'],
                ['Meters silent', num(data.operating.devices - data.operating.online), 'no data for 24h+'],
                ['Below trigger', num(data.operating.lowCustomers), 'customers need gas'],
                ['Pending orders', num(data.operating.pendingOrders), 'awaiting approval'],
              ].map(([l, v, s]) => (
                <div key={l} className="rounded-xl bg-white p-4">
                  <p className="text-xs text-muted">{l}</p>
                  <p className="num mt-1 text-2xl font-semibold">{v}</p>
                  <p className="text-xs text-ink-2">{s}</p>
                </div>
              ))}
              <Link to="/orders" search={{ status: 'pending' }} className="col-span-full text-sm font-semibold text-brand hover:underline">Review pending orders →</Link>
            </div>
          ) : (
            <LineChart
              labels={DAYS}
              format={tab === 'sales' ? nairaAxis : (v) => kg(v)}
              series={[
                { name: 'Current week', color: '#1c1c1c', values: upToToday(data.week.current.map((d) => (tab === 'sales' ? d.sales : d.kg))) },
                { name: 'Previous week', color: '#f86822', values: data.week.previous.map((d) => (tab === 'sales' ? d.sales : d.kg)) },
              ]}
            />
          )}
        </Card>
        <Card className="p-6">
          <h2 className="mb-5 text-sm font-semibold">Sales by Outlets</h2>
          {data.byOutlet.length ? <HBars data={data.byOutlet.map((o) => ({ label: o.label, value: o.kobo }))} format={nairaShort} /> : <p className="text-sm text-muted">No sales yet this period.</p>}
        </Card>
      </div>

      <div className="mt-7 grid gap-7 md:grid-cols-2">
        <Card className="min-w-0 p-6">
          <h2 className="mb-4 text-sm font-semibold">Sales by Region</h2>
          <BarChart format={nairaShort} data={data.byRegion.map((r, i) => ({ label: r.label, value: r.kobo, color: REGION_COLORS[i % REGION_COLORS.length] }))} />
        </Card>
        <Card className="p-6">
          <h2 className="mb-6 text-sm font-semibold">Sales by State</h2>
          {data.byState.length ? (
            <div className="flex flex-wrap items-center justify-around gap-6">
              <Donut data={data.byState.map((s, i) => ({ label: s.label, value: s.pct, color: STATE_COLORS[i] }))} />
              <ul className="min-w-[180px] space-y-4 text-xs">
                {data.byState.map((s, i) => (
                  <li key={s.label} className="flex items-center justify-between gap-10">
                    <span className="flex items-center gap-2"><span className="size-1.5 rounded-full" style={{ background: STATE_COLORS[i] }} />{s.label}</span>
                    <span className="num">{s.pct}%</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : <p className="text-sm text-muted">No sales yet this period.</p>}
        </Card>
      </div>
    </div>
  )
}
